/**
 * Mobile sticky add-to-cart bar for the product page.
 * Appears while the main add-to-cart button is off screen and forwards taps to it, so variant
 * selection, quantity and cart drawer behaviour all stay with the theme's own product form.
 */
class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    if (!this.section) return;

    this.button = this.querySelector('[data-sticky-atc-button]');
    this.button?.addEventListener('click', this.#onClick);
    this.querySelector('[data-sticky-atc-variant]')?.addEventListener('click', this.#scrollToForm);

    this.#watchMainButton();
    this.section.addEventListener('variant:update', this.#onVariantUpdate);
    this.#sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.disabledObserver?.disconnect();
    this.section?.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  get mainButton() {
    return this.section?.querySelector('product-form-component button[name="add"]');
  }

  #watchMainButton() {
    const main = this.mainButton;
    if (!main) return;

    this.observer?.disconnect();
    this.observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-visible', !entry?.isIntersecting);
    });
    this.observer.observe(main);

    this.disabledObserver?.disconnect();
    this.disabledObserver = new MutationObserver(() => this.#sync());
    this.disabledObserver.observe(main, { attributes: true, attributeFilter: ['disabled'] });
  }

  #onVariantUpdate = () => {
    // The section is morphed after the event fires; read the new state on the next frame.
    requestAnimationFrame(() => {
      this.#watchMainButton();
      this.#sync();
    });
  };

  #sync() {
    const main = this.mainButton;
    const productId = this.dataset.productId;
    const priceContainer = this.section?.querySelector(
      `product-price[data-product-id="${productId}"] [ref="priceContainer"]`
    );

    const price = priceContainer?.querySelector('.price')?.textContent?.trim();
    const compare = priceContainer?.querySelector('.compare-at-price')?.textContent?.trim();
    const priceEl = this.querySelector('[data-sticky-atc-price]');
    const compareEl = this.querySelector('[data-sticky-atc-compare]');
    if (priceEl && price) priceEl.textContent = price;
    if (compareEl instanceof HTMLElement) {
      compareEl.textContent = compare || '';
      compareEl.hidden = !compare;
    }

    const variantJson = this.section?.querySelector('variant-picker script[type="application/json"]')?.textContent;
    const variantEl = this.querySelector('[data-sticky-atc-variant-title]');
    if (variantJson && variantEl) {
      try {
        const variant = JSON.parse(variantJson);
        if (variant?.public_title) variantEl.textContent = variant.public_title;
      } catch (error) {
        // Leave the server-rendered variant title in place.
      }
    }

    if (this.button instanceof HTMLButtonElement && main instanceof HTMLButtonElement) {
      this.button.disabled = main.disabled;
      const label = this.querySelector('[data-sticky-atc-label]');
      if (label) label.textContent = main.disabled ? this.dataset.soldOutText : this.dataset.addText;
    }
  }

  #onClick = () => {
    const main = this.mainButton;
    if (!(main instanceof HTMLButtonElement) || main.disabled) return;
    main.click();
  };

  #scrollToForm = () => {
    const picker = this.section?.querySelector('variant-picker') || this.mainButton;
    picker?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
